import { useEffect, useState, useMemo } from "react";
import { Route, Routes, useLocation } from "react-router-dom";

import Constantes from "Projects/CC_cubi_clan_project/Utils/Constantes";
import { doFetch } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";

import BoxHomeView from "Projects/BOX_BOXconV_project/Views/BoxHomeView/BoxHomeView";
import CCONHomeView from "Projects/CCON_cubiconta_project/Views/CCONHomeView/CCONHomeView";
import HomeView from "Projects/SSO_single_sign_on_project/Views/HomeView/HomeView";
import MainView from "Projects/SSO_single_sign_on_project/Views/MainView/MainView";
import ForgotPasswordView from "Projects/SSO_single_sign_on_project/Views/ForgotPasswordView/ForgotPasswordView";
import ResetPasswordView from "Projects/SSO_single_sign_on_project/Views/ResetPasswordView/ResetPasswordView";
import TABLHomeView from "Projects/TABL_Tablerite_project/Views/TABLHomeView/TABLHomeView";
import MtaMainView from "Projects/MTA_meet_them_all_project/MtaMainView";

import MainViewTean from "Projects/TEAN_teansari_project/Views/MainView/MainView";
import MainViewMugin from "Projects/MUGIN_mugin_project/Views/MainView/MainView";

import HomeViewHapk from "Projects/HAPK_hapkiapp_project/Views/HomeView/HomeView";
import MainViewHapk from "Projects/HAPK_hapkiapp_project/Views/MainView/MainView";

import MFMMainView from "Projects/MFM_MediaFileManager_project/Views/MFMMainView/MFMMainView";
import EXHIMainView from "Projects/EXHI_exhibiciones_project/Views/EXHIMainView/EXHIMainView";
import CHAMPMainView from "Projects/CHAMP_championsjourney_project/Views/CHAMPMainView/CHAMPMainView";

import { DemoAccessPrompt } from "Projects/SSO_single_sign_on_project/Components/DemoAccessPrompt/DemoAccessPrompt";
import { AuthGate } from "Projects/SSO_single_sign_on_project/Components/AuthGate/AuthGate";

export default function App() {
  const location = useLocation();

  const currentAppCode = useMemo(() => {
    const hostname = window.location.hostname;

    // Subdominio exhibiciones.<host> → acceso EXHI por subdominio
    if (hostname.startsWith("exhibiciones.")) return "EXHI";

    if (Constantes.TEAN_ORIGINS.includes(hostname)) return "TEAN";
    if (Constantes.MUGIN_ORIGINS.includes(hostname)) return "MUGIN";
    if (Constantes.HAPK_ORIGINS.includes(hostname)) return "HAPK";

    const path = (location.pathname || "").toLowerCase();
    if (path.startsWith("/ccon")) return "CCON";
    if (path.startsWith("/tabl")) return "TABL";
    if (path.startsWith("/box")) return "BOX";
    if (path.startsWith("/hapk")) return "HAPK";
    if (path.startsWith("/mfm")) return "MFM";
    if (path.startsWith("/exhi")) return "EXHI";
    if (path.startsWith("/champ")) return "CHAMP";
    if (path.startsWith("/mta")) return "MTA";

    return null;
  }, [location.pathname]);

  const [appEstado, setAppEstado] = useState(currentAppCode ? null : "ONLINE");
  const [appTitle, setAppTitle] = useState("");

  useEffect(() => {
    if (!currentAppCode) return;
    doFetch("SSO", "getAppEstado?app=" + currentAppCode, { _silent: true })
      .then((r) => r.json())
      .then((data) => {
        if (data.STATE !== "SUCCESS" || !data.DATA) {
          setAppEstado("DISABLED");
          return;
        }
        const estado = data.DATA.estado || "DISABLED";
        setAppTitle(data.DATA.titulo || currentAppCode);

        if (estado === "DEMO") {
          // Re-validate stored demo key against BBDD
          try {
            const stored = localStorage.getItem("demo_access_" + currentAppCode);
            if (stored) {
              const parsed = JSON.parse(stored);
              if (parsed.key) {
                doFetch("SSO", "validateDemoKeyPublic", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ app: currentAppCode, key: parsed.key }),
                  _silent: true,
                })
                  .then((r) => r.json())
                  .then((res) => {
                    if (res.STATE === "SUCCESS") {
                      // Key still valid → update expiration and grant access
                      localStorage.setItem(
                        "demo_access_" + currentAppCode,
                        JSON.stringify({ key: parsed.key, expiresAt: res.DATA?.expiresAt || null })
                      );
                      setAppEstado("ONLINE");
                    } else {
                      // Key revoked or expired → remove and show prompt
                      localStorage.removeItem("demo_access_" + currentAppCode);
                      setAppEstado("DEMO");
                    }
                  })
                  .catch(() => {
                    localStorage.removeItem("demo_access_" + currentAppCode);
                    setAppEstado("DEMO");
                  });
                return; // Wait for async validation
              }
              // No key stored → remove invalid entry
              localStorage.removeItem("demo_access_" + currentAppCode);
            }
          } catch (e) {
            localStorage.removeItem("demo_access_" + currentAppCode);
          }
        }

        setAppEstado(estado);
      })
      .catch(() => setAppEstado("DISABLED"));
  }, [currentAppCode]);

  useEffect(() => {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.getElementsByTagName("head")[0].appendChild(link);
    }

    if (Constantes.TEAN_ORIGINS.includes(window.location.hostname)) {
      document.title = Constantes.TEANSARI_TITLE;
      link.href = "/images/logo/teansari-logo.png";
    } else if (Constantes.MUGIN_ORIGINS.includes(window.location.hostname)) {
      document.title = Constantes.MUGIN_TITLE;
    } else if (Constantes.HAPK_ORIGINS.includes(window.location.hostname)) {
      document.title = Constantes.HAPKIAPP_TITLE;
    } else {
      document.title = Constantes.CUBERITE_TITLE;
      link.href = "/images/logo/cuberite-logo.ico";
    }

    let themeLink = document.getElementById("theme-css");
    if (!themeLink) {
      themeLink = document.createElement("link");
      themeLink.rel = "stylesheet";
      themeLink.id = "theme-css";
      document.head.appendChild(themeLink);
    }

    if (Constantes.TEAN_ORIGINS.includes(window.location.hostname)) {
      themeLink.href = process.env.PUBLIC_URL + "/themes/tean-theme.css";
    } else if (Constantes.MUGIN_ORIGINS.includes(window.location.hostname)) {
      themeLink.href = process.env.PUBLIC_URL + "/themes/mugin-theme.css";
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (currentAppCode && appEstado === null) {
    return null;
  }

  if (currentAppCode && appEstado === "DEMO") {
    return (
      <DemoAccessPrompt
        appCode={currentAppCode}
        appTitle={appTitle}
        onAccessGranted={(data, key) => {
          try {
            localStorage.setItem(
              "demo_access_" + currentAppCode,
              JSON.stringify({ key: key, expiresAt: data?.expiresAt || null })
            );
          } catch (e) { /* localStorage not available */ }
          setAppEstado("ONLINE");
        }}
      />
    );
  }

  if (currentAppCode && (appEstado === "DISABLED" || appEstado === "INCOMPLETA")) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", flexDirection: "column", fontFamily: "sans-serif", color: "#555" }}>
        <h2>Aplicación no disponible</h2>
        <p>Esta aplicación no está disponible en este momento.</p>
      </div>
    );
  }

  // Subdominio exhibiciones.<host> → renderizar EXHIMainView con el appCode de la app host
  const hostname = window.location.hostname;
  if (hostname.startsWith("exhibiciones.")) {
    const baseHostname = hostname.replace("exhibiciones.", "");
    let hostAppCode = null;
    if (Constantes.TEAN_ORIGINS.includes(baseHostname)) hostAppCode = "TEAN";
    else if (Constantes.MUGIN_ORIGINS.includes(baseHostname)) hostAppCode = "MUGIN";
    return (
      <Routes>
        <Route path="/*" element={<EXHIMainView hostAppCode={hostAppCode} />} />
      </Routes>
    );
  }

  if (Constantes.TEAN_ORIGINS.includes(hostname)) {
    return (
      <Routes>
        <Route path="/" element={<MainViewTean />} />
        <Route path="/forgot-password" element={<ForgotPasswordView />} />
        <Route path="/reset-password" element={<ResetPasswordView />} />
        <Route path="/*" element={<MainViewTean />} />
      </Routes>
    );
  } else if (Constantes.MUGIN_ORIGINS.includes(hostname)) {
    return (
      <Routes>
        <Route path="/" element={<MainViewMugin />} />
        <Route path="/forgot-password" element={<ForgotPasswordView />} />
        <Route path="/reset-password" element={<ResetPasswordView />} />
        <Route path="/*" element={<MainViewMugin />} />
      </Routes>
    );
  } else if (Constantes.HAPK_ORIGINS.includes(hostname)) {
    return (
      <Routes>
        <Route path="/" element={<MainViewHapk />} />
        <Route path="/forgot-password" element={<ForgotPasswordView />} />
        <Route path="/reset-password" element={<ResetPasswordView />} />
        <Route path="/home/*" element={<HomeViewHapk />} />
      </Routes>
    );
  } else {
    return (
      <Routes>
        <Route path="/" element={<MainView />} />
        <Route path="/forgot-password" element={<ForgotPasswordView />} />
        <Route path="/reset-password" element={<ResetPasswordView />} />
        <Route path="/home/*" element={<AuthGate><HomeView /></AuthGate>} />
        <Route path="/portal/*" element={<AuthGate><HomeView /></AuthGate>} />
        <Route path="/box/*" element={<AuthGate><BoxHomeView /></AuthGate>} />
        <Route path="/ccon/*" element={<AuthGate><CCONHomeView /></AuthGate>} />
        <Route path="/tabl/*" element={<AuthGate><TABLHomeView /></AuthGate>} />
        <Route path="/hapk/*" element={<HomeViewHapk />} />
        <Route path="/mfm/*" element={<AuthGate><MFMMainView /></AuthGate>} />
        <Route path="/exhi/*" element={<EXHIMainView />} />
        <Route path="/champ/*" element={<AuthGate><CHAMPMainView /></AuthGate>} />
        <Route path="/mta/*" element={<AuthGate><MtaMainView /></AuthGate>} />
      </Routes>
    );
  }
}
